// useStorageLimit — "is this account out of storage?", for any upload surface.
//
// ISP-9999 / Gordon 2026-09-04: "If you are already over your subscription
// storage limit, you should not be able to upload any more media."
//
// Reads the SAME endpoint the backend gate enforces on
// (GET /api/v2/storage/usage/ via useStorageUsage), so the button state and the
// server's answer can never disagree. Server state stays in react-query — no
// hand-rolled useEffect fetch.
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useStorageUsage } from "../services/queries";
import {
  isStorageFull,
  isStorageNearLimit,
  storageNearLimitMessage,
  STORAGE_FULL_DISABLED_LABEL,
} from "../utils/storageLimit";
import { showStorageFullToast } from "../utils/storageLimitToast";
import { showNearLimitToast } from "../utils/nearLimitToast";
import { openUpgradePopup } from "../redux/reducers/upgradePopup";

/**
 * @returns {{
 *   full: boolean,          // account is at or over its limit
 *   usage: object|undefined,// the normalised usage view-model
 *   disabledLabel: string,  // why an upload control is disabled
 *   blockUpload: () => boolean  // call on any upload attempt; true = refused
 * }}
 *
 * `blockUpload()` is the one line every upload entry point needs: it shows the
 * shared toast and returns true when the account is full, so the caller can
 * `if (blockUpload()) return;` BEFORE it ever asks for a presigned URL.
 *
 * mkt-0920-dev-soft-cap-warnings: when the account is NOT yet full but is
 * approaching its limit (>=80%, see utils/nearLimit.js), blockUpload() now
 * also fires the shared soft, dismissible, once-per-session near-limit
 * toast — and still returns false, so the upload proceeds. This never
 * blocks; it is purely the "before you hit the wall" warning the storage
 * upload flow never had.
 */
export default function useStorageLimit() {
  const dispatch = useDispatch();
  const { data: usage } = useStorageUsage();
  const full = isStorageFull(usage);

  const onUpgrade = useCallback(
    // Tier 2 (Premium) is the app's standard upsell target, the same one every
    // other in-app upgrade prompt opens with.
    () => dispatch(openUpgradePopup({ requiredSubscription: 2 })),
    [dispatch]
  );

  const blockUpload = useCallback(() => {
    if (full) {
      showStorageFullToast(usage, onUpgrade);
      return true;
    }
    if (isStorageNearLimit(usage)) {
      showNearLimitToast("storage", storageNearLimitMessage(usage), onUpgrade);
    }
    return false;
  }, [full, usage, onUpgrade]);

  return {
    full,
    usage,
    disabledLabel: STORAGE_FULL_DISABLED_LABEL,
    blockUpload,
  };
}
